import request from '@/utils/request'
import { getQuestions } from '@/api/admin'

const MAX_SEARCH_TOKENS = 30
const MAX_FUZZY_SEARCH_LENGTH = 80

export default {
  namespaced: true,

  state: {
    document: null,
    contents: [],
    questionBank: [],
    loading: false,
    saving: false,
    total: 0,
    bankTotal: 0,
    currentFilter: {
      page: 1,
      pageSize: 20,
      search: '',
      category_id: null,
      question_type: null,
      question_id: null,
      matchMode: 'fuzzy', // 'fuzzy' | 'exact'
      ignoreNumbers: false // 数学题默认匹配数字
    },
    // 当前搜索的关键词（用于高亮）
    searchTokens: []
  },

  mutations: {
    SET_DOCUMENT(state, document) {
      state.document = document
    },
    SET_CONTENTS(state, contents) {
      // 确保每个内容块的工具栏默认隐藏，避免初始加载时全部显示
      state.contents = (contents || []).map(item => ({
        ...item,
        showActions: false
      }))
    },
    SET_QUESTION_BANK(state, questions) {
      state.questionBank = questions
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    SET_SAVING(state, saving) {
      state.saving = saving
    },
    SET_TOTAL(state, total) {
      state.total = total
    },
    SET_BANK_TOTAL(state, total) {
      state.bankTotal = total
    },
    SET_CURRENT_FILTER(state, filter) {
      state.currentFilter = {
        ...state.currentFilter,
        ...filter
      }
    },
    SET_SEARCH_TOKENS(state, tokens) {
      state.searchTokens = tokens
    }
  },

  actions: {
    async fetchDocument({ commit }, { fileId }) {
      try {
        commit('SET_LOADING', true)
        
        const response = await request({
          url: `/user-files/${fileId}`,
          method: 'GET',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })
        
        if (response.status === 'success') {
          const { file } = response.data
          if (!file) {
            throw new Error('文件不存在或无权访问')
          }
          commit('SET_DOCUMENT', file)
          commit('SET_CONTENTS', file.content || [])
        }
      } catch (error) {
        console.error('获取文档失败:', error)
        // 清空文档状态
        commit('SET_DOCUMENT', null)
        commit('SET_CONTENTS', [])
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async addContent({ commit }, { fileId, content, options = {} }) {
      try {
        commit('SET_SAVING', true)
        const response = await request({
          url: `/file-contents/${fileId}/content`,
          method: 'POST',
          data: content
        })

        if (response.status === 'success') {
          if (options.skipFetch) {
            return response.data?.content || null
          }
          // 重新获取最新的文档内容
          const docResponse = await request({
            url: `/user-files/${fileId}`,
            method: 'GET',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          })

          if (docResponse.status === 'success') {
            const { file } = docResponse.data
            commit('SET_DOCUMENT', file)
            commit('SET_CONTENTS', file.content || [])
            return file
          }
        }
      } catch (error) {
        console.error('添加内容失败:', error)
        throw error
      } finally {
        commit('SET_SAVING', false)
      }
    },

    async addContents({ commit }, { fileId, contents }) {
      try {
        commit('SET_SAVING', true)
        const response = await request({
          url: `/file-contents/${fileId}/contents`,
          method: 'POST',
          data: { contents }
        })
        
        if (response.status === 'success') {
          const { file } = response.data
          commit('SET_DOCUMENT', file)
          commit('SET_CONTENTS', file.content || [])
        }
      } catch (error) {
        console.error('批量添加内容失败:', error)
        throw error
      } finally {
        commit('SET_SAVING', false)
      }
    },

    async updateContent({ commit }, { fileId, contentId, content, options = {} }) {
      try {
        console.log('正在尝试更新内容:', { fileId, contentId, content })  // 添加这行日志
        commit('SET_SAVING', true)
        const response = await request({
          url: `/file-contents/${fileId}/content/${contentId}`,
          method: 'PUT',
          data: content
        })
        
        if (response.status === 'success' && !options.skipFetch) {
          // 重新获取最新的文档内容
          const docResponse = await request({
            url: `/user-files/${fileId}`,
            method: 'GET',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          })
          
          if (docResponse.status === 'success') {
            const { file } = docResponse.data
            commit('SET_DOCUMENT', file)
            commit('SET_CONTENTS', file.content || [])
          }
        }
      } catch (error) {
        console.error('更新内容失败:', error)
        throw error
      } finally {
        commit('SET_SAVING', false)
      }
    },

    // AI 精简手写解析:把当前解析文本交给后端 qwen,返回精简结果字符串。
    // 无副作用;调用方拿到结果后自行走 saveHandwritingDraft 保存。
    async simplifyAnalysis(_, { source, documentId, templateId, inlineTemplate, question, answer }) {
      const res = await request({
        url: '/file-contents/simplify-analysis',
        method: 'POST',
        data: { source, documentId, templateId, inlineTemplate, question, answer },
        timeout: 600000
      })
      return res && res.data ? res.data.simplified : ''
    },

    async generateHandwritingAnalysis(_, { description, question, answer, originalAnalysis, currentAnalysis }) {
      const res = await request({
        url: '/file-contents/generate-handwriting-analysis',
        method: 'POST',
        data: { description, question, answer, originalAnalysis, currentAnalysis },
        timeout: 600000
      })
      return res && res.data ? res.data.generated : ''
    },

    async getHandwritingSimplifyConfig() {
      const res = await request({
        url: '/file-contents/simplify-analysis/config',
        method: 'GET'
      })
      return res && res.data ? res.data : {}
    },

    async removeContent({ commit }, { fileId, contentId }) {
      try {
        const response = await request({
          url: `/file-contents/${fileId}/content/${contentId}`,
          method: 'DELETE'
        })

        if (response.status === 'success') {
          // 重新获取最新的文档内容
          const docResponse = await request({
            url: `/user-files/${fileId}`,
            method: 'GET',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          })
          
          if (docResponse.status === 'success') {
            const { file } = docResponse.data
            commit('SET_DOCUMENT', file)
            commit('SET_CONTENTS', file.content || [])
          }
        }
      } catch (error) {
        console.error('删除内容失败:', error)
        throw error
      }
    },

    async reorderContents({ commit }, { fileId, contentOrders, options = {} }) {
      try {
        commit('SET_SAVING', true)
        const response = await request({
          url: `/file-contents/${fileId}/reorder`,
          method: 'PUT',
          data: { content_orders: contentOrders }
        })

        if (response.status === 'success') {
          if (options.skipFetch) {
            return response.data || null
          }
          // 重新获取最新的文档内容
          const docResponse = await request({
            url: `/user-files/${fileId}`,
            method: 'GET',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          })
          
          if (docResponse.status === 'success') {
            const { file } = docResponse.data
            commit('SET_DOCUMENT', file)
            commit('SET_CONTENTS', file.content || [])
          }
        }
      } catch (error) {
        console.error('更新顺序失败:', error)
        throw error
      } finally {
        commit('SET_SAVING', false)
      }
    },

    // 更新文档设置（如页眉页脚）
    async updateDocument({ commit }, { id, data }) {
      try {
        commit('SET_SAVING', true)
        const response = await request({
          url: `/user-files/${id}`,
          method: 'PUT',
          data
        })
        
        if (response.status === 'success') {
          // 更新本地 document 数据
          const docResponse = await request({
            url: `/user-files/${id}`,
            method: 'GET'
          })
          
          if (docResponse.status === 'success') {
            const { file } = docResponse.data
            commit('SET_DOCUMENT', file)
          }
        }
        return response
      } catch (error) {
        console.error('更新文档失败:', error)
        throw error
      } finally {
        commit('SET_SAVING', false)
      }
    },

    async fetchQuestionBank({ commit, state }, params = {}) {
      try {
        // 解析搜索文本，判断是否为精确搜索
        const searchText = params.search ?? state.currentFilter.search
        let matchMode = state.currentFilter.matchMode
        let actualSearch = searchText
        const ignoreNumbers = params.ignoreNumbers ?? state.currentFilter.ignoreNumbers
        
        // 检测双引号精确搜索
        if (searchText && searchText.trim().match(/^"(.+)"$/)) {
          matchMode = 'exact'
          actualSearch = searchText.trim().slice(1, -1) // 去掉引号
        }

        if (matchMode === 'fuzzy' && actualSearch) {
          actualSearch = actualSearch.slice(0, MAX_FUZZY_SEARCH_LENGTH)
        }
        
        // 提取搜索关键词用于高亮
        // 模糊搜索：按单个字符拆分（与后端保持一致）
        // 精确搜索：保持完整字符串
        let tokens = []
        if (actualSearch) {
          if (matchMode === 'exact') {
            tokens = [actualSearch]
          } else {
            // 移除空白字符，按单个字符拆分，去重
            let chars = actualSearch.replace(/\s+/g, '').split('')
            // 如果忽略数字，高亮时也过滤掉数字
            if (ignoreNumbers) {
              chars = chars.filter(c => !/\d/.test(c))
            }
            tokens = [...new Set(chars)].filter(Boolean).slice(0, MAX_SEARCH_TOKENS)
          }
        }
        commit('SET_SEARCH_TOKENS', tokens)
        
        const searchParams = {
          page: params.page ?? state.currentFilter.page,
          pageSize: params.pageSize ?? state.currentFilter.pageSize,
          category_id: params.category_id ?? state.currentFilter.category_id,
          question_type: params.question_type ?? state.currentFilter.question_type,
          search: actualSearch,
          matchMode,
          ignoreNumbers,
          question_id: params.question_id ?? state.currentFilter.question_id ?? null
        }
        
        console.log('获取题库参数:', searchParams)
        const response = await getQuestions(searchParams)
        
        if (response.status === 'success') {
          const questions = response.data.questions || []
          // 兼容两种返回格式：response.data.total 或 response.data.pagination.total_items
          const total = response.data.total ?? response.data.pagination?.total_items ?? questions.length
          
          commit('SET_QUESTION_BANK', questions)
          commit('SET_BANK_TOTAL', total)
          console.log('题库数据:', {
            questions,
            total,
            currentPage: searchParams.page,
            pageSize: searchParams.pageSize
          })
          return {
            questions,
            total
          }
        }
      } catch (error) {
        console.error('获取题库失败:', error)
        throw error
      }
    }
  },

  getters: {
    documentTitle: state => state.document?.name || '',
    contents: state => state.contents,
    bankTotal: state => state.bankTotal,
    currentFilter: state => state.currentFilter,
    searchTokens: state => state.searchTokens
  }
}
